import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Tenant metadata stored client-side.
 * Extended information not included in session.
 */
export interface TenantMetadata {
  id: string;
  name: string;
  // Add more fields as needed (feature flags, subscription tier, etc.)
}

/**
 * Tenant store state and actions.
 *
 * This store manages tenant context client-side, complementing
 * better-auth's session which handles authentication only.
 *
 * Use cases:
 * - Active tenant selection (for multi-tenant users)
 * - Tenant switching UX (loading states, optimistic updates)
 * - Recent tenants list for quick switching
 * - Tenant metadata caching
 */
interface TenantState {
  // Current active tenant
  activeTenantId: string | null;

  // Tenant metadata cache
  tenants: Record<string, TenantMetadata>;

  // Recent tenants for quick switching (LRU, max 5)
  recentTenantIds: string[];

  // Switching state for UX
  isSwitching: boolean;
  switchingToId: string | null;

  // Actions
  setActiveTenant: (tenantId: string, metadata?: TenantMetadata) => void;
  setTenants: (tenants: TenantMetadata[]) => void;
  clearTenant: () => void;
  startSwitching: (tenantId: string) => void;
  finishSwitching: () => void;
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      activeTenantId: null,
      tenants: {},
      recentTenantIds: [],
      isSwitching: false,
      switchingToId: null,

      setActiveTenant: (tenantId, metadata) => {
        const { recentTenantIds, tenants } = get();

        // Update recent tenants (LRU)
        const newRecent = [
          tenantId,
          ...recentTenantIds.filter((id) => id !== tenantId),
        ].slice(0, 5);

        // Update metadata if provided
        const newTenants = metadata
          ? { ...tenants, [tenantId]: metadata }
          : tenants;

        set({
          activeTenantId: tenantId,
          recentTenantIds: newRecent,
          tenants: newTenants,
          isSwitching: false,
          switchingToId: null,
        });
      },

      setTenants: (tenantList) => {
        const { activeTenantId } = get();
        const newTenants: Record<string, TenantMetadata> = {};
        for (const tenant of tenantList) {
          newTenants[tenant.id] = tenant;
        }
        // If current activeTenantId is not in the new tenant list, reset it
        const shouldResetActive = activeTenantId && !newTenants[activeTenantId];
        set({
          tenants: newTenants,
          ...(shouldResetActive ? { activeTenantId: null } : {}),
        });
      },

      clearTenant: () => {
        set({
          activeTenantId: null,
          isSwitching: false,
          switchingToId: null,
        });
      },

      startSwitching: (tenantId) => {
        set({ isSwitching: true, switchingToId: tenantId });
      },

      finishSwitching: () => {
        set({ isSwitching: false, switchingToId: null });
      },
    }),
    {
      name: "folio-tenant-storage",
      // Only persist these fields
      partialize: (state) => ({
        activeTenantId: state.activeTenantId,
        recentTenantIds: state.recentTenantIds,
        tenants: state.tenants,
      }),
    }
  )
);

// Selector hooks for common patterns
export const useActiveTenant = () => {
  const { activeTenantId, tenants } = useTenantStore();
  return activeTenantId ? tenants[activeTenantId] : null;
};

export const useActiveTenantId = () => useTenantStore((s) => s.activeTenantId);

export const useIsSwitchingTenant = () =>
  useTenantStore((s) => s.isSwitching);
